import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActionLink, Canceled, ClientActionsWrapper, LeaderAddress } from './styles'
import { ArrowRightIcon, ArrowDownIcon } from 'components/Icons'
import { Domiciliation } from 'globals/models/Domiciliation'
import { useLazyQuery } from '@apollo/client'
import {
  CUSTOMER_ZIP,
  CustomerZipResult,
  CustomerZipVariables,
  POSTAL_PROCURATION_PDF,
  PostalProcurationResult,
  PostalProcurationVariables,
  MailTagSheetResult
} from 'pages/ClientDetails/components/ClientActionsTab/queries'
import { SetState } from 'globals'
import { format } from 'date-fns'
import ClientValidation from 'pages/ClientDetails/components/ClientActionsTab/components/ClientValidation'
import { displayErrors } from 'globals/graphql'
import { WaitingModal } from './components/waitingModal'
import BelgiumPPConfirmation from './components/belgiumPPConfirmation'
import ClientParcelModal from 'pages/ClientDetails/components/ClientActionsTab/components/ClientParcelModal'

interface IProps {
  domiciliation: Domiciliation
  fetchMails: any
}

const ClientActionsTab = (props: IProps) => {
  const { domiciliation, fetchMails } = props
  const { t } = useTranslation()
  const ownAddress = domiciliation.address?.ownAddress
  const [showAddress, setShowAddress] = useState(false)
  const [waitingModalVisible, setWaitingModalVisible] = useState(false)
  const [showParcelModal, setShowParcelModal] = useState(false)
  let pdfCurrentUuid: string | null = null

  const customerZipRetrieved = (result: CustomerZipResult) => {
    if (result.error) {
      displayErrors.onCompleted(result)
    } else if (result?.customerZip) {
      setWaitingModalVisible(false)
      window.location.assign(result.customerZip)
    } else {
      setWaitingModalVisible(true)
      setTimeout(
        () =>
          getCustomerZip({
            variables: {
              domiciliationId: domiciliation.id,
              generateZip: false
            }
          }),
        5000
      )
    }
  }

  const postalProcurationPdfRetrieved = (result: PostalProcurationResult) => {
    if (result?.postalProcurationPdf?.uuid) {
      pdfCurrentUuid = result.postalProcurationPdf.uuid
    }

    if (result.error) {
      pdfCurrentUuid = null
      setWaitingModalVisible(false)
      displayErrors.onCompleted(result)
    } else if (result?.postalProcurationPdf?.url) {
      pdfCurrentUuid = null
      setWaitingModalVisible(false)
      window.open(result.postalProcurationPdf.url, '_blank') || window.location.replace(result.postalProcurationPdf.url)
    } else if (pdfCurrentUuid) {
      setWaitingModalVisible(true)
      setTimeout(
        () =>
          getPostalProcurationPdf({
            variables: {
              domiciliationId: domiciliation.id,
              uuid: pdfCurrentUuid
            }
          }),
        5000
      )
    } else {
      pdfCurrentUuid = null
      setWaitingModalVisible(false)
      displayErrors.onCompleted(
        "Erreur lors de l'obtention de la procuration postale, veuillez nous contacter s'il vous plait"
      )
    }
  }

  const [getPostalProcurationPdf] = useLazyQuery<PostalProcurationResult, PostalProcurationVariables>(
    POSTAL_PROCURATION_PDF,
    { ...displayErrors, onCompleted: postalProcurationPdfRetrieved, fetchPolicy: 'no-cache' }
  )

  const [getCustomerZip] = useLazyQuery<CustomerZipResult, CustomerZipVariables>(CUSTOMER_ZIP, {
    ...displayErrors,
    onCompleted: customerZipRetrieved,
    fetchPolicy: 'no-cache'
  })

  return domiciliation.status === 'pending_partner_check' ? (
    <ClientValidation domiciliation={domiciliation} customerZipRetrieved={customerZipRetrieved} />
  ) : (
    <ClientActionsWrapper>
      <ClientParcelModal
        visible={showParcelModal}
        onClose={() => setShowParcelModal(false)}
        domiciliation={domiciliation}
        fetchMails={fetchMails}
      />
      {domiciliation.printablePostalProcuration && (
        <ActionLink
          onClick={() => getPostalProcurationPdf({ variables: { domiciliationId: domiciliation.id, uuid: null } })}
        >
          {t('clients.actions.print')}
          <ArrowRightIcon />
        </ActionLink>
      )}

      {!ownAddress && (
        <ActionLink
          onClick={() =>
            getCustomerZip({
              variables: {
                domiciliationId: domiciliation.id,
                generateZip: true
              }
            })
          }
        >
          {t('clients.actions.download')}
          <ArrowRightIcon />
        </ActionLink>
      )}

      {!ownAddress && (
        <ActionLink onClick={toggleShowLeaderAddress(setShowAddress)}>
          {t('clients.actions.show')}
          {showAddress ? <ArrowDownIcon /> : <ArrowRightIcon />}
        </ActionLink>
      )}

      <ActionLink onClick={() => setShowParcelModal(true)}>
        {t('clients.actions.parcel.title')}
        {showAddress ? <ArrowDownIcon /> : <ArrowRightIcon />}
      </ActionLink>

      {!ownAddress && showAddress && (
        <LeaderAddress>
          {domiciliation.leaderAddress.streetAddress},{domiciliation.leaderAddress.postalCode}
          {domiciliation.leaderAddress.city}
        </LeaderAddress>
      )}

      {domiciliation.status === 'canceled' && (
        <Canceled>
          {t('clients.actions.canceled-at', { date: format(new Date(domiciliation.canceledAt), 'dd/MM/yyyy') })}
        </Canceled>
      )}

      <WaitingModal setWaitingModalVisible={setWaitingModalVisible} waitingModalVisible={waitingModalVisible} />

      {domiciliation.countryCode === 'BE' && <BelgiumPPConfirmation domiciliation={domiciliation} />}
    </ClientActionsWrapper>
  )
}

export const mailTagSheetQueryRetrieved = (result: MailTagSheetResult) => {
  displayErrors.onCompleted(result)

  if (result?.mailTagSheet) {
    window.open(result.mailTagSheet, '_blank')?.focus()
  }
}

const toggleShowLeaderAddress = (setShowAddress: SetState<boolean>) => () =>
  setShowAddress((showAddress) => {
    return !showAddress
  })

export default ClientActionsTab
